import { useCallback, useEffect, useRef, useState } from "react";
import { actionsWSUrl } from "./api";
import type { UITreeResponse } from "../types";

export interface TapAction {
  type: "tap";
  x: number;
  y: number;
}

export interface DragAction {
  type: "drag";
  fromX: number;
  fromY: number;
  toX: number;
  toY: number;
  duration?: number;
  pressDuration?: number;
  holdDuration?: number;
}

export interface PressKeyAction {
  type: "press-key";
  /** "home", "back", "recent_apps", "volume_up", "volume_down", … */
  key: string;
}

export interface RefreshAction {
  type: "refresh";
}

export type ActionMsg = TapAction | DragAction | PressKeyAction | RefreshAction;

export interface ActionsWS {
  /** Latest UI tree pushed by the server (null until the first push). */
  tree: UITreeResponse | null;
  /** True while an open WebSocket is available to accept sends. */
  connected: boolean;
  /** Fire-and-forget — silently dropped if the socket isn't open. */
  send: (msg: ActionMsg) => void;
}

/**
 * Opens a long-lived WebSocket to the server's manual-actions channel
 * for a single device. Reconnects automatically with backoff if the
 * socket drops (laptop sleep, server restart, etc).
 *
 * Actions sent through this channel are quota-exempt; the server
 * pushes a debounced UI tree back after each action, which we hand
 * back to the caller as `tree`.
 */
export function useActionsWS(deviceId: string | null): ActionsWS {
  const [tree, setTree] = useState<UITreeResponse | null>(null);
  const [connected, setConnected] = useState(false);
  const wsRef = useRef<WebSocket | null>(null);

  useEffect(() => {
    if (!deviceId) {
      setTree(null);
      setConnected(false);
      return;
    }

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let retryDelay = 500;

    const connect = () => {
      if (cancelled) return;
      const ws = new WebSocket(actionsWSUrl(deviceId));
      wsRef.current = ws;

      ws.onopen = () => {
        if (cancelled) {
          ws.close();
          return;
        }
        retryDelay = 500;
        setConnected(true);
      };

      ws.onmessage = (ev) => {
        if (cancelled) return;
        try {
          const data = JSON.parse(ev.data);
          if (data && data.type === "ui-tree" && data.tree) {
            setTree(data.tree as UITreeResponse);
          }
        } catch {
          /* ignore malformed frames */
        }
      };

      ws.onerror = () => {
        /* onclose handles reconnection */
      };

      ws.onclose = () => {
        if (wsRef.current === ws) wsRef.current = null;
        if (cancelled) return;
        setConnected(false);
        // Exponential backoff capped at 10s — covers server restart,
        // flaky Wi-Fi, etc. without hammering when the server is
        // genuinely down.
        retryTimer = setTimeout(connect, retryDelay);
        retryDelay = Math.min(retryDelay * 2, 10_000);
      };
    };

    connect();

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (wsRef.current) {
        const ws = wsRef.current;
        wsRef.current = null;
        ws.close();
      }
    };
  }, [deviceId]);

  const send = useCallback((msg: ActionMsg) => {
    const ws = wsRef.current;
    if (!ws || ws.readyState !== WebSocket.OPEN) return;
    try {
      ws.send(JSON.stringify(msg));
    } catch {
      /* connection probably just died; next reconnect will resync */
    }
  }, []);

  return { tree, connected, send };
}
